/**
 * PressReleasesExtractionsContext
 *
 * Provides a React context and two convenience hooks for working with
 * press release extraction datapoints in the issue form. The module defines the
 * `FieldProvenance` and `PressReleasesExtractionsContextValue` types, supplies a sensible
 * `emptyValue` default, and exports `PressReleasesExtractionsContext` plus
 * `useSuggestionDatapointsVisibility` and `usePressReleasesExtractions` hooks for consuming
 * visibility state, extracted fields, and provenance metadata from the
 * surrounding form UI.
 */
import React from 'react'

export type FieldProvenance = {
    sourceUrl: string | null
    pressReleaseId: string | null
    sourcePages: number[]
    extractionId: string | null
    quote: string | null
}

type PressReleasesExtractionsContextValue = {
    showSuggestionDatapoints: boolean
    companyId: string | null
    suggestionImportantDates: Record<string, string | null>
    suggestionOfferingTerms: Record<string, number | string | null>
    suggestionOfferingOutcome: Record<string, number | string | null>
    suggestionProvenanceByField: Record<string, FieldProvenance>
}

const emptyValue: PressReleasesExtractionsContextValue = {
    showSuggestionDatapoints: false,
    companyId: null,
    suggestionImportantDates: {},
    suggestionOfferingTerms: {},
    suggestionOfferingOutcome: {},
    suggestionProvenanceByField: {},
}

export const PressReleasesExtractionsContext = React.createContext<PressReleasesExtractionsContextValue>(emptyValue)

export const useSuggestionDatapointsVisibility = () => {
    const context = React.useContext(PressReleasesExtractionsContext)
    return {
        showSuggestionDatapoints: context.showSuggestionDatapoints,
        companyId: context.companyId,
    }
}

export const usePressReleasesExtractions = () => {
    const context = React.useContext(PressReleasesExtractionsContext)
    return {
        companyId: context.companyId,
        suggestionImportantDates: context.suggestionImportantDates,
        suggestionOfferingTerms: context.suggestionOfferingTerms,
        suggestionOfferingOutcome: context.suggestionOfferingOutcome,
        suggestionProvenanceByField: context.suggestionProvenanceByField,
    }
}
